import { useState, useEffect, useCallback, useMemo } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { ledgerAPI, prospectsAPI } from '../lib/api';
import { 
    Calculator, FileText, CheckCircle, Clock, Plus, DollarSign, RefreshCw, 
    Search, Filter, TrendingUp, ArrowRight, Loader2, Download, PieChart,
    ArrowUpRight, ArrowDownRight, BarChart3
} from 'lucide-react';
import { CreateCommissionDialog } from './commission/CreateCommissionDialog';
import { SimulationDialog } from './commission/SimulationDialog';

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
};

const PAYMENT_MODES = {
    valnexo_only: 'Valnexo 100%',
    at_only: 'AT Service 100%',
    split_50_50: 'Split 50/50'
};

export default function CommissionLedger() {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('commissions');
    const [commissions, setCommissions] = useState([]);
    const [ledgerEntries, setLedgerEntries] = useState([]);
    const [prospects, setProspects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [showSimDialog, setShowSimDialog] = useState(false);
    const [simResult, setSimResult] = useState(null);
    const [selectedComm, setSelectedComm] = useState(null);
    const [validating, setValidating] = useState(false);
    
    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [pipelineFilter, setPipelineFilter] = useState('all');
    
    const [form, setForm] = useState({ 
        dossier_id: '', 
        pipeline_type: 'easy_power', 
        montant_global: '', 
        vp_payment_mode: 'split_50_50' 
    });
    
    const isAdmin = user?.role === 'admin_full' || user?.role === 'admin' || user?.is_admin === true;
    const canValidate = (user?.role === 'admin_full' || user?.is_admin === true);
    
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [c, e, p] = await Promise.all([
                ledgerAPI.getCommissions(),
                ledgerAPI.getLedgerEntries(),
                prospectsAPI.getAll({ limit: 200 })
            ]);
            setCommissions(c.data || []);
            setLedgerEntries(e.data || []);
            setProspects(p.data || []);
        } catch (err) {
            toast.error('Erreur chargement');
        } finally {
            setLoading(false);
        }
    }, []);
    
    useEffect(() => { loadData(); }, [loadData]);
    
    // Filtered commissions
    const filteredCommissions = useMemo(() => {
        return commissions.filter(c => {
            const matchesSearch = !searchTerm || 
                c.dossier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                c.vp_name?.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesStatus = statusFilter === 'all' || c.statut === statusFilter;
            const matchesPipeline = pipelineFilter === 'all' || c.pipeline_type === pipelineFilter;
            return matchesSearch && matchesStatus && matchesPipeline;
        });
    }, [commissions, searchTerm, statusFilter, pipelineFilter]);
    
    // Filtered ledger entries
    const filteredLedger = useMemo(() => {
        return ledgerEntries.filter(e => {
            const matchesSearch = !searchTerm || 
                e.dossier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                e.payeur_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                e.beneficiaire_name?.toLowerCase().includes(searchTerm.toLowerCase());
            return matchesSearch;
        });
    }, [ledgerEntries, searchTerm]);
    
    // Stats calculations
    const stats = useMemo(() => {
        const total = commissions.length;
        const draftCount = commissions.filter(c => c.statut === 'draft').length;
        const validCount = commissions.filter(c => c.statut === 'validee').length;
        const totalMontant = commissions.reduce((s, c) => s + (c.montant_global || 0), 0);
        const validMontant = commissions.filter(c => c.statut === 'validee').reduce((s, c) => s + (c.montant_global || 0), 0);
        const epCount = commissions.filter(c => c.pipeline_type === 'easy_power').length;
        const chCount = commissions.filter(c => c.pipeline_type === 'chantier').length;
        const epMontant = commissions.filter(c => c.pipeline_type === 'easy_power').reduce((s, c) => s + (c.montant_global || 0), 0);
        const chMontant = commissions.filter(c => c.pipeline_type === 'chantier').reduce((s, c) => s + (c.montant_global || 0), 0);
        
        return { total, draftCount, validCount, totalMontant, validMontant, epCount, chCount, epMontant, chMontant };
    }, [commissions]);
    
    const handleCreate = async () => {
        if (!form.dossier_id || !form.montant_global) {
            toast.error('Remplissez tous les champs');
            return;
        }
        try {
            await ledgerAPI.createCommission({
                dossier_id: form.dossier_id,
                pipeline_type: form.pipeline_type,
                montant_global: parseFloat(form.montant_global),
                vp_payment_mode: form.vp_payment_mode
            });
            toast.success('Commission creee');
            setShowCreateDialog(false);
            setForm({ dossier_id: '', pipeline_type: 'easy_power', montant_global: '', vp_payment_mode: 'split_50_50' });
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        }
    };
    
    const handleSimulate = async (comm) => {
        setSelectedComm(comm);
        try {
            const res = await ledgerAPI.simulateCommission(comm.id);
            setSimResult(res.data);
            setShowSimDialog(true);
        } catch (err) {
            toast.error('Erreur simulation');
        }
    };
    
    const handleValidate = async () => {
        if (!selectedComm) return;
        setValidating(true);
        try {
            const res = await ledgerAPI.validateCommission(selectedComm.id);
            toast.success(res.data.message);
            setShowSimDialog(false);
            loadData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Erreur');
        } finally {
            setValidating(false);
        }
    };
    
    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="commission-ledger-page">
                {/* Header */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-outfit font-semibold text-slate-900">Commission Ledger</h1>
                        <p className="text-slate-500 mt-1">Gestion des commissions et ecritures comptables</p>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={loadData} data-testid="refresh-btn">
                            <RefreshCw className="w-4 h-4 mr-2" />Actualiser
                        </Button>
                        {isAdmin && (
                            <Button onClick={() => setShowCreateDialog(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="new-commission-btn">
                                <Plus className="w-4 h-4 mr-2" />Nouvelle Commission
                            </Button>
                        )}
                    </div>
                </div>
                
                {/* Stats Cards - Improved Design */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200">
                        <CardContent className="pt-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">Total Commissions</p>
                                    <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total}</p>
                                    <div className="flex items-center gap-2 mt-2 text-xs">
                                        <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded">EP: {stats.epCount}</span>
                                        <span className="text-amber-600 bg-amber-50 px-2 py-0.5 rounded">CH: {stats.chCount}</span>
                                    </div>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-slate-200 flex items-center justify-center">
                                    <Calculator className="w-6 h-6 text-slate-600" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
                        <CardContent className="pt-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-medium text-amber-600 uppercase tracking-wider">Brouillons</p>
                                    <p className="text-3xl font-bold text-amber-700 mt-1">{stats.draftCount}</p>
                                    <p className="text-xs text-amber-600 mt-2">En attente de validation</p>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-amber-200 flex items-center justify-center">
                                    <Clock className="w-6 h-6 text-amber-700" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                        <CardContent className="pt-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">Validees</p>
                                    <p className="text-3xl font-bold text-blue-700 mt-1">{stats.validCount}</p>
                                    <p className="text-xs text-blue-600 mt-2">{formatCurrency(stats.validMontant)}</p>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-blue-200 flex items-center justify-center">
                                    <CheckCircle className="w-6 h-6 text-blue-700" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    
                    <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
                        <CardContent className="pt-5">
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="text-xs font-medium text-emerald-600 uppercase tracking-wider">Montant Total</p>
                                    <p className="text-2xl font-bold text-emerald-700 mt-1">{formatCurrency(stats.totalMontant)}</p>
                                    <div className="flex items-center gap-1 mt-2">
                                        <TrendingUp className="w-3 h-3 text-emerald-600" />
                                        <span className="text-xs text-emerald-600">Cumul global</span>
                                    </div>
                                </div>
                                <div className="w-12 h-12 rounded-xl bg-emerald-200 flex items-center justify-center">
                                    <DollarSign className="w-6 h-6 text-emerald-700" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
                
                {/* Pipeline Distribution */}
                {stats.total > 0 && (
                    <Card>
                        <CardContent className="pt-5">
                            <div className="flex items-center gap-3 mb-4">
                                <BarChart3 className="w-5 h-5 text-slate-500" />
                                <h3 className="font-semibold text-slate-900">Repartition par Pipeline</h3>
                            </div>
                            <div className="grid grid-cols-2 gap-6">
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-blue-700">Easy Power</span>
                                        <span className="text-sm text-slate-500">{stats.epCount} commissions</span>
                                    </div>
                                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full transition-all"
                                            style={{ width: `${stats.total > 0 ? (stats.epMontant / stats.totalMontant) * 100 : 0}%` }}
                                        />
                                    </div>
                                    <p className="text-lg font-bold text-blue-700 mt-2">{formatCurrency(stats.epMontant)}</p>
                                </div>
                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-medium text-amber-700">Chantier</span>
                                        <span className="text-sm text-slate-500">{stats.chCount} commissions</span>
                                    </div>
                                    <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
                                        <div 
                                            className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all"
                                            style={{ width: `${stats.total > 0 ? (stats.chMontant / stats.totalMontant) * 100 : 0}%` }}
                                        />
                                    </div>
                                    <p className="text-lg font-bold text-amber-700 mt-2">{formatCurrency(stats.chMontant)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}
                
                {/* Filters */}
                <Card className="border-slate-200">
                    <CardContent className="pt-4">
                        <div className="flex flex-col lg:flex-row gap-4">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input 
                                    placeholder="Rechercher un dossier, VP, beneficiaire..."
                                    className="pl-10"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    data-testid="search-input"
                                />
                            </div>
                            <div className="flex gap-2">
                                <Select value={statusFilter} onValueChange={setStatusFilter}>
                                    <SelectTrigger className="w-40" data-testid="status-filter">
                                        <SelectValue placeholder="Statut" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les statuts</SelectItem>
                                        <SelectItem value="draft">Brouillons</SelectItem>
                                        <SelectItem value="validee">Validees</SelectItem>
                                    </SelectContent>
                                </Select>
                                <Select value={pipelineFilter} onValueChange={setPipelineFilter}>
                                    <SelectTrigger className="w-40" data-testid="pipeline-filter">
                                        <SelectValue placeholder="Pipeline" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tous les pipelines</SelectItem>
                                        <SelectItem value="easy_power">Easy Power</SelectItem>
                                        <SelectItem value="chantier">Chantier</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                    </CardContent>
                </Card>
                
                {/* Tabs */}
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="bg-slate-100">
                        <TabsTrigger value="commissions" className="gap-2" data-testid="tab-commissions">
                            <Calculator className="w-4 h-4" />Commissions
                            {filteredCommissions.length > 0 && (
                                <Badge variant="secondary" className="ml-1">{filteredCommissions.length}</Badge>
                            )}
                        </TabsTrigger>
                        <TabsTrigger value="ledger" className="gap-2" data-testid="tab-ledger">
                            <FileText className="w-4 h-4" />Ecritures Ledger
                            {filteredLedger.length > 0 && (
                                <Badge variant="secondary" className="ml-1">{filteredLedger.length}</Badge>
                            )}
                        </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="commissions" className="mt-4">
                        <CommissionTableEnhanced 
                            commissions={filteredCommissions}
                            loading={loading}
                            onSimulate={handleSimulate}
                        />
                    </TabsContent>
                    
                    <TabsContent value="ledger" className="mt-4">
                        <LedgerTableEnhanced entries={filteredLedger} />
                    </TabsContent>
                </Tabs>
            </div>
            
            <CreateCommissionDialog
                open={showCreateDialog}
                onOpenChange={setShowCreateDialog}
                form={form}
                setForm={setForm}
                prospects={prospects}
                onCreate={handleCreate}
            />
            
            <SimulationDialog
                open={showSimDialog}
                onOpenChange={setShowSimDialog}
                selectedComm={selectedComm}
                simResult={simResult}
                canValidate={canValidate}
                validating={validating}
                onValidate={handleValidate}
            />
        </DashboardLayout>
    );
}

function CommissionTableEnhanced({ commissions, loading, onSimulate }) {
    if (loading) {
        return (
            <Card>
                <CardContent className="py-12">
                    <div className="flex flex-col items-center justify-center">
                        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                        <p className="mt-2 text-sm text-slate-500">Chargement des commissions...</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    if (commissions.length === 0) {
        return (
            <Card>
                <CardContent className="py-12">
                    <div className="text-center">
                        <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">Aucune commission trouvee</p>
                        <p className="text-sm text-slate-400 mt-1">Modifiez vos filtres ou creez une nouvelle commission</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent className="p-0">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold">Dossier</TableHead>
                            <TableHead className="font-semibold">Pipeline</TableHead>
                            <TableHead className="font-semibold">VP</TableHead>
                            <TableHead className="font-semibold text-center">Taux VP</TableHead>
                            <TableHead className="font-semibold">Mode Paiement</TableHead>
                            <TableHead className="font-semibold text-right">Montant</TableHead>
                            <TableHead className="font-semibold text-center">Statut</TableHead>
                            <TableHead className="font-semibold text-center">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {commissions.map((c) => {
                            const isEP = c.pipeline_type === 'easy_power';
                            const isValidee = c.statut === 'validee';
                            
                            return (
                                <TableRow key={c.id} className="hover:bg-slate-50/50">
                                    <TableCell>
                                        <div>
                                            <p className="font-medium text-slate-900">{c.dossier_name}</p>
                                            <p className="text-xs text-slate-400">{formatDate(c.created_at)}</p>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <Badge className={isEP ? 'bg-blue-100 text-blue-700 border-blue-200' : 'bg-amber-100 text-amber-700 border-amber-200'}>
                                            {isEP ? 'Easy Power' : 'Chantier'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                                                <span className="text-xs font-semibold text-emerald-700">
                                                    {c.vp_name?.split(' ').map(n => n[0]).join('')}
                                                </span>
                                            </div>
                                            <span className="text-sm">{c.vp_name}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <Badge className="bg-emerald-50 text-emerald-700 font-mono">{c.vp_commission_percent}%</Badge>
                                    </TableCell>
                                    <TableCell>
                                        <span className="text-xs text-slate-600">{PAYMENT_MODES[c.vp_payment_mode]}</span>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <span className="font-bold text-slate-900">{formatCurrency(c.montant_global)}</span>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <Badge className={isValidee ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-600'}>
                                            {isValidee ? 'Validee' : 'Brouillon'}
                                        </Badge>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <Button variant="outline" size="sm" onClick={() => onSimulate(c)} data-testid={`simulate-btn-${c.id}`}>
                                            <Calculator className="w-4 h-4" />
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}

function LedgerTableEnhanced({ entries }) {
    if (entries.length === 0) {
        return (
            <Card>
                <CardContent className="py-12">
                    <div className="text-center">
                        <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                        <p className="text-slate-500 font-medium">Aucune ecriture trouvee</p>
                        <p className="text-sm text-slate-400 mt-1">Les ecritures apparaissent apres validation des commissions</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <Card>
            <CardContent className="p-0">
                <Table>
                    <TableHeader>
                        <TableRow className="bg-slate-50">
                            <TableHead className="font-semibold">Dossier</TableHead>
                            <TableHead className="font-semibold">Type</TableHead>
                            <TableHead className="font-semibold">Payeur</TableHead>
                            <TableHead className="w-10"></TableHead>
                            <TableHead className="font-semibold">Beneficiaire</TableHead>
                            <TableHead className="font-semibold text-right">Montant</TableHead>
                            <TableHead className="font-semibold text-center">Statut</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {entries.map((e) => {
                            const isPaid = e.statut === 'paid';
                            
                            return (
                                <TableRow key={e.id} className="hover:bg-slate-50/50">
                                    <TableCell>
                                        <p className="font-medium text-slate-900">{e.dossier_name}</p>
                                    </TableCell>
                                    <TableCell>
                                        <span className="text-xs text-slate-600 bg-slate-100 px-2 py-1 rounded">{e.type_entry}</span>
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center">
                                                <ArrowUpRight className="w-3 h-3 text-red-600" />
                                            </div>
                                            <span className="text-sm">{e.payeur_name}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell>
                                        <ArrowRight className="w-4 h-4 text-slate-300" />
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center">
                                                <ArrowDownRight className="w-3 h-3 text-green-600" />
                                            </div>
                                            <span className="text-sm">{e.beneficiaire_name}</span>
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-right">
                                        <span className="font-bold text-slate-900">{formatCurrency(e.montant_final)}</span>
                                    </TableCell>
                                    <TableCell className="text-center">
                                        <Badge className={isPaid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                                            {isPaid ? 'Paye' : 'Du'}
                                        </Badge>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}
